/**
 * MapaVendedorV2Preview.jsx — Versão 2
 * Layout idêntico ao MapaCompradorV2Preview.jsx, adaptado para vendedor
 */
import { useRef, useState, Fragment } from 'react'
import { Printer } from 'lucide-react'
import api from '../../api/client'
import AlterarComissaoModal from '../../components/modals/AlterarComissaoModal'
import AdicionarDespesaModal from '../../components/modals/AdicionarDespesaModal'
import IsentarDefesaModal from '../../components/modals/IsentarDefesaModal'
import AplicarIsencaoModal from '../../components/modals/AplicarIsencaoModal'
import { openPdf, downloadPdf } from '../../utils/pdfUtils'
import { criasTxt, criasTotais } from './criasUtils'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const BLUE_HDR = '#1a3a5c'
const BLUE_TXT = '#1a5276'

const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const num = v => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { const d = String(s).split('T')[0]; const [y,m,dd] = d.split('-'); return (y && m && dd) ? `${dd}/${m}/${y}` : new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

const S = {
  page:     { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#000', background: '#fff', padding: '16px 24px 20px' },
  hdr:      { display: 'flex', alignItems: 'center', gap: 20, marginBottom: 6, paddingBottom: 6 },
  hdrLogo:  { maxHeight: 64, maxWidth: 160, objectFit: 'contain' },
  hdrT1:    { fontSize: 18, fontWeight: 700, color: BLUE_TXT },
  hdrT2:    { fontSize: 14, color: '#333', marginTop: 3, fontWeight: 700 },
  hdrT3:    { fontSize: 13, color: '#666', marginTop: 1 },
  vendNome: { fontSize: 20, fontWeight: 700, textAlign: 'center', marginBottom: 2, padding: '1.5px 0', border: '1.5px solid #333' },
  vendGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 13, marginBottom: 12, padding: '8px 0' },
  vendLbl:  { color: BLUE_TXT, fontWeight: 700 },
  secTitle: { fontSize: 13, fontWeight: 700, color: '#000000', marginBottom: 3, marginTop: 12, textAlign: 'center', textTransform: 'uppercase' },
  tbl:      { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 8 },
  thBlue:   { background: '#f0f0f0', color: '#000', padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: 12, border: '1px solid #ddd' },
  td:       { border: '1px solid #ddd', padding: '5px 8px', verticalAlign: 'top' },
  tdC:      { border: '1px solid #ddd', padding: '5px 8px', textAlign: 'center', verticalAlign: 'top' },
  tdR:      { border: '1px solid #ddd', padding: '5px 8px', textAlign: 'right', verticalAlign: 'top' },
  resumo:   { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0, border: '1px solid #ddd', marginTop: 10, marginBottom: 12 },
  resumoCol:{ padding: '10px 14px', borderRight: '1px solid #ddd', fontSize: 13, lineHeight: 1.6, textAlign: 'center' },
  resumoLbl:{ color: BLUE_TXT, fontWeight: 700 },
  footer:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16, paddingTop: 10, fontSize: 13 },
  footerSite:{ fontSize: 13, fontWeight: 700, color: BLUE_TXT, marginTop: 8 },
}

function Sec({ title, children }) {
  return <div>
    {title && <div style={S.secTitle}>{title}</div>}
    {children}
  </div>
}

const DEFAULT_COLUNAS_V2_VEND = ['lote', 'curral', 'comprador', 'situacao', 'qtd', 'descricao', 'valor_unitario', 'total', 'liquido', 'comissao']

function MapaPageV2({ vend, leilao, organizador, colunasAtivas = DEFAULT_COLUNAS_V2_VEND }) {
  const mostrarCurral = colunasAtivas.includes('curral')
  const mostrarPct    = colunasAtivas.includes('pct')
  const lotesVenda = vend.lotes_venda || []
  const lotesDefesa = vend.lotes_defesa || []
  const lotesNaoMov = vend.lotes_nao_movimentados || []
  const criasTot = criasTotais([...lotesVenda, ...lotesDefesa, ...lotesNaoMov])

  let totalVv = 0, totalCv = 0, totalDesp = 0, totalLiq = 0, totalDesconto = 0
  let qtdVendas = 0, qtdAnimais = 0
  let totalFemea = 0, qtdFemea = 0, totalMacho = 0, qtdMacho = 0, totalMisto = 0, qtdMisto = 0

  lotesVenda.forEach(l => {
    const vv = l.vv || 0; const qtd = l.qtd || 0
    const cv = l.cv_val || 0
    const liq = vv - cv
    totalVv += vv; totalCv += cv; totalDesp += l.desp; totalDesconto += (l.desconto || 0)
    totalLiq += l.liquido; qtdVendas++; qtdAnimais += qtd
    const sexo = (l.sexo || '').toUpperCase()
    if (sexo === 'FEMEA' || sexo === 'FÊMEA') { totalFemea += liq; qtdFemea += qtd }
    else if (sexo === 'MACHO') { totalMacho += liq; qtdMacho += qtd }
    else { totalMisto += liq; qtdMisto += qtd }
  })

  // Despesas de lotes em defesa também entram no total de despesas do vendedor
  let totalDefVv = 0, totalDefCv = 0, qtdDefAnimais = 0
  lotesDefesa.forEach(l => {
    totalDefVv += l.vv || 0
    totalDefCv += l.cv_val || 0
    totalDesp  += l.desp || 0
    qtdDefAnimais += l.qtd || 0
  })

  // Totais de não movimentados
  let qtdNaoMovAnimais = 0
  lotesNaoMov.forEach(l => {
    qtdNaoMovAnimais += l.qtd || 0
  })

  // Total geral de animais (vendas + defesas + não movimentados)
  const qtdAnimaisGeral = qtdAnimais + qtdDefAnimais + qtdNaoMovAnimais

  const mediaFemea  = qtdFemea ? totalFemea / qtdFemea : 0
  const mediaMacho  = qtdMacho ? totalMacho / qtdMacho : 0
  const mediaMisto  = qtdMisto ? totalMisto / qtdMisto : 0
  const mediaGeral  = qtdAnimais > 0 ? (totalVv - totalCv - totalDesconto) / qtdAnimais : 0

  // Agrupa parcelas por comprador
  const compradoresMap = {}
  lotesVenda.forEach(l => {
    const key = l.comprador_nome || '-'
    if (!compradoresMap[key]) {
      compradoresMap[key] = { nome: key, cpf: l.comprador_cpf || '', lotes: [], parcelas: [], totalParcelas: 0 }
    }
    compradoresMap[key].lotes.push(l.numero_lote)
    ;(l.parcelas || []).forEach(p => {
      compradoresMap[key].parcelas.push({ ...p, numero_lote: l.numero_lote })
      compradoresMap[key].totalParcelas += p.liquido || 0
    })
  })

  // Ordena parcelas de cada comprador por data e agrupa por data de vencimento
  const normalizeDate = (d) => {
    if (!d) return ''
    const s = String(d)
    if (s.includes('T')) return s.split('T')[0]
    return s.slice(0, 10)
  }
  Object.values(compradoresMap).forEach(c => {
    c.parcelas.sort((a, b) => {
      if (!a.data) return 1
      if (!b.data) return -1
      return new Date(a.data) - new Date(b.data)
    })
    // Agrupa parcelas com mesma data
    const grouped = {}
    c.parcelas.forEach(p => {
      const key = normalizeDate(p.data)
      if (grouped[key]) {
        grouped[key].liquido += p.liquido || 0
        if (p.numero_lote && !grouped[key].lotes_parcela.includes(p.numero_lote)) {
          grouped[key].lotes_parcela.push(p.numero_lote)
        }
      } else {
        grouped[key] = { ...p, data: normalizeDate(p.data), lotes_parcela: [p.numero_lote].filter(Boolean) }
      }
    })
    c.parcelas = Object.values(grouped).sort((a, b) => (a.data || '').localeCompare(b.data || ''))
    c.totalParcelas = c.parcelas.reduce((s, p) => s + (p.liquido || 0), 0)
  })

  const orgDb = organizador.dados_bancarios
  const logoSrc = organizador.logo_url
    ? (organizador.logo_url.startsWith('http') ? organizador.logo_url
        : `${API_BASE}${organizador.logo_url.startsWith('/') ? '' : '/'}${organizador.logo_url}`)
    : null

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.hdr}>
        <div style={{ width: 160 }}>{logoSrc ? <img src={logoSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700, fontSize: 16, color: BLUE_TXT }}>{organizador.nome}</span>}</div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={S.hdrT1}>Mapa do Vendedor</div>
          <div style={S.hdrT2}>{leilao.nome} - {fmtDate(leilao.data_leilao)}</div>
          <div style={S.hdrT3}>{organizador.nome}</div>
        </div>
        <div style={{ width: 160, textAlign: 'right' }}>
          {leilao.banner_url && (
            <img
              src={leilao.banner_url.startsWith('http') ? leilao.banner_url : `${API_BASE}${leilao.banner_url.startsWith('/') ? '' : '/'}${leilao.banner_url}`}
              alt="banner leilão"
              style={{ maxHeight: 64, maxWidth: 160, objectFit: 'contain', marginLeft: 'auto' }}
            />
          )}
        </div>
      </div>

      {/* Nome vendedor */}
      <div style={S.vendNome}>{vend.nome.toUpperCase()}</div>

      {/* Dados vendedor — 3 colunas */}
      <div style={S.vendGrid}>
        <div>
          <div><span style={S.vendLbl}>CPF/CNPJ:</span> {vend.cpf_cnpj || '—'}</div>
          <div><span style={S.vendLbl}>Endereço:</span> {vend.endereco || '—'}</div>
          <div><span style={S.vendLbl}>Bairro:</span> {vend.bairro || '—'}</div>
          <div><span style={S.vendLbl}>Município:</span> {vend.cidade ? `${vend.cidade} - ${vend.uf || ''}` : '—'}</div>
        </div>
        <div>
          <div><span style={S.vendLbl}>CEP:</span> {vend.cep || '—'}</div>
          <div><span style={S.vendLbl}>Telefone:</span> {vend.telefone || '—'}</div>
          <div><span style={S.vendLbl}>Email:</span> {vend.email || '—'}</div>
        </div>
        <div>
          <div><span style={S.vendLbl}>Fazenda:</span> {vend.fazenda || '—'}</div>
          <div><span style={S.vendLbl}>I.E.:</span> {vend.fazenda_ie || '—'}</div>
          <div><span style={S.vendLbl}>Cidade/UF:</span> {vend.fazenda_cidade ? `${vend.fazenda_cidade} - ${vend.fazenda_uf || ''}` : '—'}</div>
        </div>
      </div>

      {/* Lotes */}
        <table style={{ ...S.tbl, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '5%' }} />
            {mostrarCurral && <col style={{ width: '6%' }} />}
            <col style={{ width: mostrarCurral ? '17%' : '21%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '4%' }} />
            <col style={{ width: mostrarPct ? '17%' : '20%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: mostrarPct ? '10%' : '12%' }} />
            {mostrarPct && <col style={{ width: '6%' }} />}
          </colgroup>
          <thead>
            <tr>
              <th style={S.thBlue}>Lote</th>
              {mostrarCurral && <th style={S.thBlue}>Curral</th>}
              <th style={S.thBlue}>Comprador</th>
              <th style={S.thBlue}>Sit</th>
              <th style={S.thBlue}>Qtd</th>
              <th style={S.thBlue}>Descrição</th>
              <th style={S.thBlue}>Unitário</th>
              <th style={S.thBlue}>Total</th>
              <th style={S.thBlue}>Líquido</th>
              <th style={S.thBlue}>Comissões</th>
              {mostrarPct && <th style={S.thBlue}>%</th>}
            </tr>
          </thead>
          <tbody>
            {lotesVenda.map((l, i) => {
              const pesoStr = l.peso ? `${Number(l.peso).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}kg` : ''
              const descLine1 = [l.tipo_animal, l.raca].filter(Boolean).join(' ').toUpperCase()
              const descLine2 = [l.sexo, l.idade, pesoStr].filter(Boolean).join(' ').toUpperCase()
              const desc = [descLine1, descLine2].filter(Boolean).join('\n')
              const liq  = (l.parcelas || []).reduce((s, p) => s + (p.liquido || 0), 0)
              return (
                <Fragment key={i}>
                <tr style={{ background: i % 2 === 0 ? '#fff' : '#f7f9fc' }}>
                  <td style={{ ...S.tdC, fontWeight: 700 }}>{l.numero_lote}</td>
                  {mostrarCurral && <td style={S.tdC}>{l.curral || '—'}</td>}
                  <td style={{ ...S.td, wordBreak: 'break-word' }}>{l.comprador_nome || '—'}</td>
                  <td style={{ ...S.tdC, whiteSpace: 'nowrap' }}>VEN</td>
                  <td style={S.tdC}>{l.qtd}</td>
                  <td style={{ ...S.tdC, whiteSpace: 'pre-line' }}>{desc}</td>
                  <td style={{ ...S.tdR, whiteSpace: 'nowrap' }}>{num(l.vv_unit || 0)}</td>
                  <td style={{ ...S.tdR, whiteSpace: 'nowrap' }}>{num(l.vv || 0)}</td>
                  <td style={{ ...S.tdR, whiteSpace: 'nowrap' }}>{num(liq)}</td>
                  <td style={{ ...S.tdC, fontSize: 11, whiteSpace: 'nowrap' }}>V: {brl(l.cv_val || 0)}</td>
                  {mostrarPct && <td style={{ ...S.td, fontSize: 11, whiteSpace: 'nowrap' }}>V: {(l.cv_pct || 0).toFixed(2)}%</td>}
                </tr>
                {criasTxt(l) && (
                  <tr style={{ background: i % 2 === 0 ? '#fff' : '#f7f9fc' }}>
                    <td colSpan={mostrarCurral ? (mostrarPct ? 11 : 10) : (mostrarPct ? 10 : 9)} style={{ ...S.td, fontSize: 10, padding: '1px 6px' }}>
                      <strong>Crias:</strong> {criasTxt(l)}
                    </td>
                  </tr>
                )}
                </Fragment>
              )
            })}
            {/* Lotes de defesa na mesma tabela */}
            {lotesDefesa.length > 0 && (
              <>
                <tr><td colSpan={mostrarCurral ? (mostrarPct ? 11 : 10) : (mostrarPct ? 10 : 9)} style={{ background: '#fff3e0', fontWeight: 700, fontSize: 11, padding: '6px 8px', borderTop: '2px solid #ff9800', color: '#e65100' }}>DEFESAS</td></tr>
                {lotesDefesa.map((l, i) => {
                  const desc = [l.tipo_animal, l.raca, l.sexo, l.idade].filter(Boolean).join(' ').toUpperCase()
                  return (
                    <Fragment key={`def-${i}`}>
                    <tr style={{ background: '#fff8e1' }}>
                      <td style={{ ...S.tdC, fontWeight: 700 }}>{l.numero_lote}</td>
                      {mostrarCurral && <td style={S.tdC}>{l.curral || '—'}</td>}
                      <td style={S.td}>{l.comprador_nome || '—'}</td>
                      <td style={{ ...S.tdC, color: '#e65100', fontWeight: 700 }}>DEF</td>
                      <td style={S.tdC}>{l.qtd}</td>
                      <td style={{ ...S.tdC, whiteSpace: 'pre-line' }}>{desc}</td>
                      <td style={S.tdR}>{num(l.vv_unit)}</td>
                      <td style={S.tdR}>{num(l.vv)}</td>
                      <td style={S.tdR}>—</td>
                      <td style={{ ...S.tdC, fontSize: 11 }}>D: {brl(l.cv_val || 0)}</td>
                      {mostrarPct && <td style={{ ...S.td, fontSize: 11 }}>D: {(l.cv_pct || 0).toFixed(2)}%</td>}
                    </tr>
                    {criasTxt(l) && (
                      <tr style={{ background: '#fff8e1' }}>
                        <td colSpan={mostrarCurral ? (mostrarPct ? 11 : 10) : (mostrarPct ? 10 : 9)} style={{ ...S.td, fontSize: 10, padding: '1px 6px' }}>
                          <strong>Crias:</strong> {criasTxt(l)}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  )
                })}
              </>
            )}
          </tbody>
        </table>

        {/* Lotes não movimentados / defesa */}
        {lotesNaoMov.length > 0 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#e65100', marginTop: 10, marginBottom: 4, paddingTop: 6, borderTop: '2px solid #ff9800' }}>
              Lotes Não Movimentados / Defesa
            </div>
            <table style={{ ...S.tbl, tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ ...S.thBlue, width: '8%' }}>Lote</th>
                  <th style={{ ...S.thBlue, width: '10%' }}>Sit</th>
                  <th style={{ ...S.thBlue, width: '8%' }}>Qtd</th>
                  <th style={S.thBlue}>Descrição</th>
                  <th style={{ ...S.thBlue, width: '15%' }}>Valor</th>
                  <th style={{ ...S.thBlue, width: '15%' }}>Comissão</th>
                </tr>
              </thead>
              <tbody>
                {lotesNaoMov.map((l, i) => {
                  const pesoStr = l.peso ? `${Number(l.peso).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}kg` : ''
                  const descLine1 = [l.tipo_animal, l.raca].filter(Boolean).join(' ').toUpperCase()
                  const descLine2 = [l.sexo, l.idade, pesoStr].filter(Boolean).join(' ').toUpperCase()
                  const desc = [descLine1, descLine2].filter(Boolean).join(' ')
                  const sitLabel = l.situacao === 'DEFESA' ? 'DEF' : 'N/M'
                  return (
                    <Fragment key={i}>
                    <tr style={{ background: '#fff3e0' }}>
                      <td style={{ ...S.tdC, fontWeight: 700 }}>{l.numero_lote}</td>
                      <td style={{ ...S.tdC, color: '#e65100', fontWeight: 700 }}>{sitLabel}</td>
                      <td style={S.tdC}>{l.qtd}</td>
                      <td style={S.tdC}>{desc}</td>
                      <td style={S.tdC}>{l.vv ? brl(l.vv) : '—'}</td>
                      <td style={S.tdC}>{l.cv_val ? brl(l.cv_val) : '—'}</td>
                    </tr>
                    {criasTxt(l) && (
                      <tr style={{ background: '#fff3e0' }}>
                        <td colSpan={6} style={{ ...S.td, fontSize: 10, padding: '1px 6px' }}>
                          <strong>Crias:</strong> {criasTxt(l)}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </>
        )}
      {/* Resumo — 3 colunas */}
        <div style={S.resumo}>
          <div style={S.resumoCol}>
            <div><span style={S.resumoLbl}>Vendas:</span> Lotes: {qtdVendas}, Qtde: {qtdAnimais}, Valor: {brl(totalVv)}</div>
            {(() => {
              const todosDef = [...lotesDefesa, ...lotesNaoMov.filter(l => l.situacao === 'DEFESA')]
              const todosNm = lotesNaoMov.filter(l => l.situacao !== 'DEFESA')
              const soma = (arr) => arr.reduce((s, l) => s + (l.qtd || 0), 0)
              const valor = (arr) => arr.reduce((s, l) => s + (l.vv || 0), 0)
              return (
                <>
                  {todosDef.length > 0 && <div><span style={S.resumoLbl}>Defesas:</span> Lotes: {todosDef.length}, Qtde: {soma(todosDef)}, Valor: {brl(valor(todosDef))}</div>}
                  {todosNm.length > 0 && <div><span style={S.resumoLbl}>Não Mov.:</span> Lotes: {todosNm.length}, Qtde: {soma(todosNm)}{valor(todosNm) > 0 ? `, Valor: ${brl(valor(todosNm))}` : ''}</div>}
                </>
              )
            })()}
            <div><span style={S.resumoLbl}>Quantidade de Animais:</span> {qtdAnimaisGeral}</div>
            {criasTot.txt && <div><span style={S.resumoLbl}>Crias:</span> {criasTot.txt}</div>}
            <div><span style={S.resumoLbl}>Despesas:</span> {brl(totalDesp)}</div>
          </div>
          <div style={S.resumoCol}>
            <div><span style={S.resumoLbl}>Comissão Venda:</span> {brl(totalCv)}</div>
            {totalDefCv > 0 && <div><span style={S.resumoLbl}>Comissão Defesa:</span> {brl(totalDefCv)}</div>}
            {totalDesconto > 0 && <div style={{ fontWeight: 700 }}><span style={S.resumoLbl}>Descontos À Vista:</span> -{brl(totalDesconto)}</div>}
          </div>
          <div style={{ ...S.resumoCol, borderRight: 'none' }}>
            <div><span style={S.resumoLbl}>Média Fêmea:</span> {brl(mediaFemea)}</div>
            <div><span style={S.resumoLbl}>Média Macho:</span> {brl(mediaMacho)}</div>
            <div><span style={S.resumoLbl}>Média Misto:</span> {brl(mediaMisto)}</div>
            <div><span style={S.resumoLbl}>Média Geral:</span> {brl(mediaGeral)}</div>
          </div>
        </div>

      {/* Pagamentos Compradores */}
      <Sec title="Pagamentos Compradores">
        <table style={{ ...S.tbl, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '28%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '30%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={S.thBlue}>Comprador</th>
              <th style={S.thBlue}>Lotes</th>
              <th style={S.thBlue}>Pagamento</th>
              <th style={S.thBlue}>Prazo</th>
              <th style={S.thBlue}>Valor Total</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(compradoresMap).map((c, ci) => {
              const parcCount = c.parcelas.length || 1
              return c.parcelas.map((p, pi) => (
                <tr key={`${ci}-${pi}`} style={{ background: ci % 2 === 0 ? '#fff' : '#f7f9fc' }}>
                  {pi === 0 && (
                    <>
                      <td style={{ ...S.tdC, verticalAlign: 'top', borderBottom: '1px solid #ddd' }} rowSpan={parcCount}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{c.nome}</div>
                        {c.cpf && <div style={{ fontSize: 12 }}>CPF/CNPJ: {c.cpf}</div>}
                        <div style={{ fontSize: 12 }}>Total de parcelas: {parcCount}</div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>Valor total: {brl(c.totalParcelas)}</div>
                      </td>
                    </>
                  )}
                  <td style={{ ...S.tdC, borderBottom: '1px solid #eee' }}>{(p.lotes_parcela || []).join(', ')}</td>
                  <td style={{ ...S.tdC, borderBottom: '1px solid #eee' }}>
                    <div style={{ fontWeight: 700 }}>{brl(p.liquido)}</div>
                    <div style={{ fontSize: 11 }}>Venc: {fmtDate(p.data)}</div>
                  </td>
                  <td style={{ ...S.tdC, borderBottom: '1px solid #eee' }}>{p.prazo}</td>
                  {pi === 0 && (
                    <td style={{ ...S.tdC, verticalAlign: 'top', fontSize: 16, fontWeight: 700, borderBottom: '1px solid #ddd' }} rowSpan={parcCount}>
                      {brl(c.totalParcelas)}
                    </td>
                  )}
                </tr>
              ))
            })}
          </tbody>
        </table>
      </Sec>

      {/* Pagamentos Comissões — só quando fluxo é separada */}
      {leilao.fluxo_comissao === 'separada' && totalCv > 0 && (
      <Sec title="Pagamento Comissão">
        <table style={S.tbl}>
          <thead>
            <tr>{['Organizador','Valor','Dados Bancários'].map(h => <th key={h} style={S.thBlue}>{h}</th>)}</tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...S.tdC, whiteSpace: 'nowrap' }}>
                <div style={{ fontWeight: 700 }}>{organizador.nome}</div>
                {organizador.cnpj && <div style={{ fontSize: 12 }}>CNPJ: {organizador.cnpj}</div>}
                {(organizador.cidade || organizador.uf) && (
                  <div style={{ fontSize: 12 }}>{[organizador.cidade, organizador.uf].filter(Boolean).join(' - ')}</div>
                )}
              </td>
              <td style={{ ...S.tdC, fontWeight: 700, whiteSpace: 'nowrap', fontSize: 14, color: BLUE_TXT }}>
                <div>Comissão Venda: {brl(totalCv)}</div>
                {totalDefCv > 0 && <div>Comissão Defesa: {brl(totalDefCv)}</div>}
                {totalDesp > 0 && <div>Despesas: {brl(totalDesp)}</div>}
                <div style={{ marginTop: 4, fontSize: 16 }}><strong>Total: {brl(totalCv + totalDefCv + totalDesp)}</strong></div>
              </td>
              <td style={{ ...S.tdC, whiteSpace: 'nowrap' }}>
                {orgDb ? (
                  <div style={{ fontSize: 12 }}>
                    {orgDb.banco   && <div>Banco: {orgDb.banco}</div>}
                    {orgDb.pix     && <div>PIX: {orgDb.pix}</div>}
                    {orgDb.agencia && <div>AG: {orgDb.agencia} C/C: {orgDb.conta}</div>}
                  </div>
                ) : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </Sec>
      )}

      {/* Pagamento Comissão Defesa — só quando há defesas com comissão E não foi consolidado acima */}
      {(() => {
        if (!lotesDefesa.length) return null
        const totalDefCv = lotesDefesa.reduce((s, l) => s + (l.cv_val || 0), 0)
        if (totalDefCv <= 0) return null
        // Quando separada+vendas, a comissão defesa já aparece consolidada em "Pagamento Comissão"
        if (leilao.fluxo_comissao === 'separada' && totalCv > 0) return null

        // GTA+Nota em defesa
        const fluxoLeilao = leilao.fluxo_comissao || ''
        const cobrarDefesa = leilao.gta_cobrar_defesa || false
        let gtaNotaDef = 0
        if (fluxoLeilao === 'gta_nota' && cobrarDefesa) {
          const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
          const valorNota = parseFloat(leilao.valor_nota) || 0
          const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'
          const qtdDefAnimais = lotesDefesa.reduce((s, l) => s + (l.qtd || 0), 0)
          if (valorPorAnimal > 0 || valorNota > 0) {
            const gtaTotal = valorPorAnimal * qtdDefAnimais
            const notaTotal = gtaModo === 'por_lote' ? valorNota * lotesDefesa.length : valorNota * 1
            gtaNotaDef = gtaTotal + notaTotal
          }
        }

        const totalPagar = totalDefCv + gtaNotaDef

        return (
          <Sec title="Pagamento Comissão Defesa">
            <table style={S.tbl}>
              <thead>
                <tr>{['Organizador','Valor','Dados Bancários'].map(h => <th key={h} style={S.thBlue}>{h}</th>)}</tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...S.tdC, whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 700 }}>{organizador.nome}</div>
                    {organizador.cnpj && <div style={{ fontSize: 12 }}>CNPJ: {organizador.cnpj}</div>}
                    {(organizador.cidade || organizador.uf) && (
                      <div style={{ fontSize: 12 }}>{[organizador.cidade, organizador.uf].filter(Boolean).join(' - ')}</div>
                    )}
                  </td>
                  <td style={{ ...S.tdC, fontWeight: 700, whiteSpace: 'nowrap', fontSize: 14, color: BLUE_TXT }}>
                    <div>Comissão Defesa: {brl(totalDefCv)}</div>
                    {gtaNotaDef > 0 && <div>GTA + Nota: {brl(gtaNotaDef)}</div>}
                    <div style={{ marginTop: 4, fontSize: 16 }}><strong>Total: {brl(totalPagar)}</strong></div>
                  </td>
                  <td style={{ ...S.tdC, whiteSpace: 'nowrap' }}>
                    {orgDb ? (
                      <div style={{ fontSize: 12 }}>
                        {orgDb.banco   && <div>Banco: {orgDb.banco}</div>}
                        {orgDb.pix     && <div>PIX: {orgDb.pix}</div>}
                        {orgDb.agencia && <div>AG: {orgDb.agencia} C/C: {orgDb.conta}</div>}
                      </div>
                    ) : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </Sec>
        )
      })()}

      {/* TOTAL GERAL */}
      <div style={{ border: '1px solid #999', marginTop: 12, marginBottom: 8 }}>
        <div style={{ background: '#f0f0f0', borderBottom: '1px solid #999', padding: '2px 0', textAlign: 'center', fontWeight: 700, fontSize: 13 }}>
          TOTAL GERAL
        </div>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
            <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Animais</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{qtdAnimaisGeral}</div>
            {criasTot.txt && <div style={{ fontSize: 10, color: '#555' }}>Crias: {criasTot.txt}</div>}
          </div>
          <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
            <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Vendido</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalVv)}</div>
          </div>
          <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
            <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Comissões</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalCv + totalDefCv + totalDesp)}</div>
          </div>
          <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Líquido</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalLiq)}</div>
          </div>
        </div>
      </div>

      {/* Rodapé */}
      <div style={{ ...S.footer, flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 40 }}>
        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }} />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ borderTop: '1px solid #000', width: 220, margin: '0 auto', paddingTop: 4 }}>{vend.nome.toUpperCase()}</div>
          </div>
          <div style={{ flex: 1, textAlign: 'right', fontSize: 11 }}>Declaro que estou de acordo<br />com as especificações acima.</div>
        </div>
      </div>
      <div style={S.footerSite}>rematei.com.br</div>
    </div>
  )
}

export default function MapaVendedorV2Preview({ dados, colunasAtivas = DEFAULT_COLUNAS_V2_VEND, orgId, leilaoId, vendedorId, isencaoDefesaAtiva = false, onReload }) {
  const iframeRef = useRef(null)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [showAlterarComissao, setShowAlterarComissao] = useState(false)
  const [showDespesa, setShowDespesa] = useState(false)
  const [showIsencao, setShowIsencao] = useState(null)
  const [showAplicarIsencao, setShowAplicarIsencao] = useState(false)

  if (!dados) return null
  const { leilao, organizador, vendedores } = dados

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const qs = new URLSearchParams()
      if (vendedorId) qs.set('vendedor_id', vendedorId)
      qs.set('versao', '2')
      qs.set('mostrar_curral', String(colunasAtivas.includes('curral')))
      qs.set('mostrar_pct', String(colunasAtivas.includes('pct')))
      const resp = await api.get(
        `/organizadores/${orgId}/mapas/leilao/${leilaoId}/mapa-vendedor?${qs}`,
        { responseType: 'blob', timeout: 60000 }
      )
      const blob = new Blob([resp.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally { setLoadingPdf(false) }
  }

  const closePdfModal = () => { setShowPdfModal(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }
  const pdfFilename = `Mapa do Vendedor V2 - ${vendedores.length === 1 ? vendedores[0].nome : `Todos (${vendedores.length})`}.pdf`.replace(/[/\\?%*:|"<>]/g, '-')

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <p className="text-sm text-gray-600 flex-1 min-w-0 truncate">
          <span className="font-medium text-gray-800">{leilao.nome}</span>
          {' · '}{vendedores.length === 1 ? vendedores[0].nome : `${vendedores.length} vendedores`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handleImprimir} disabled={loadingPdf}
            className="flex items-center gap-1.5 px-4 py-2.5 sm:px-3 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-xs font-semibold rounded-lg transition-colors disabled:opacity-60">
            <Printer className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">{loadingPdf ? 'Gerando...' : 'Imprimir'}</span>
            <span className="sm:hidden">{loadingPdf ? 'Gerando...' : 'PDF'}</span>
          </button>
          {vendedores.length === 1 && (
            <button onClick={async () => {
              try {
                const resp = await api.get(
                  `/organizadores/${orgId}/recibos/mapa-vendedor/${leilaoId}/${vendedores[0].id}/recibo-pagamento`,
                  { responseType: 'blob', timeout: 30000 }
                )
                const blob = new Blob([resp.data], { type: 'application/pdf' })
                if (pdfUrl) URL.revokeObjectURL(pdfUrl)
                openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
              } catch (err) {
                import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de pagamento'))
              }
            }}
              className="flex items-center gap-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-lg transition-colors"
              title="Recibo de pagamento do valor líquido ao vendedor">
              Recibo Pagamento
            </button>
          )}
          {vendedores.length === 1 && leilao.fluxo_comissao === 'separada' && (
            <button onClick={async () => {
              try {
                const resp = await api.get(
                  `/organizadores/${orgId}/recibos/mapa-vendedor/${leilaoId}/${vendedores[0].id}/recibo-comissao`,
                  { responseType: 'blob', timeout: 30000 }
                )
                const blob = new Blob([resp.data], { type: 'application/pdf' })
                if (pdfUrl) URL.revokeObjectURL(pdfUrl)
                openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
              } catch (err) {
                import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de comissão'))
              }
            }}
              className="flex items-center gap-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded-lg transition-colors"
              title="Recibo de recebimento de comissão + despesas">
              Recibo Comissão
            </button>
          )}
          {vendedores.length === 1 && (
            <button onClick={() => setShowAlterarComissao(true)}
              className="hidden sm:flex items-center gap-1 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
              title="Alterar comissão de venda dos lotes">
              Alterar Com.
            </button>
          )}
          {vendedores.length === 1 && (
            <button onClick={() => setShowDespesa(true)}
              className="flex items-center gap-1 px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-xs font-medium rounded-lg transition-colors"
              title="Adicionar despesa nos lotes do vendedor">
              + Despesa
            </button>
          )}
          {/* Isentar/Desfazer Defesa */}
          {vendedores.length === 1 && (() => {
            const lotesDefesa = vendedores[0].lotes_defesa || vendedores[0].lotes_nao_movimentados?.filter(l => l.situacao === 'DEFESA') || []
            const temComissaoDefesa = lotesDefesa.some(l =>
              l.cv_val > 0 && !l.defesa_isenta && (l.defesa_elegivel === null || l.defesa_elegivel === true)
            )
            const temIsento = lotesDefesa.some(l => l.defesa_isenta)
            const loteComPct = lotesDefesa.find(l => l.defesa_pct_defendido !== null && l.defesa_pct_defendido !== undefined)
            return (
              <>
                {isencaoDefesaAtiva && loteComPct && (
                  <span className="flex items-center px-2 py-2 text-sm font-semibold text-gray-800" title="% defendido (em valor) desse vendedor nesse leilão">
                    defendeu {loteComPct.defesa_pct_defendido.toFixed(2)}%
                  </span>
                )}
                {temComissaoDefesa && (
                  <button onClick={() => setShowIsencao('isentar')}
                    className="flex items-center gap-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors"
                    title="Zerar comissão de defesa">
                    Isentar Defesa
                  </button>
                )}
                {temIsento && (
                  <button onClick={() => setShowIsencao('desfazer')}
                    className="flex items-center gap-1 px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded-lg transition-colors"
                    title="Restaurar comissão de defesa original">
                    Desfazer Isenção
                  </button>
                )}
              </>
            )
          })()}
          {isencaoDefesaAtiva && (
            <button onClick={() => setShowAplicarIsencao(true)}
              className="flex items-center gap-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors"
              title="Sugerir e aplicar isenção de defesa pros vendedores elegíveis do leilão inteiro (não só o selecionado aqui)">
              Aplicar Isenção (leilão)
            </button>
          )}
        </div>
      </div>

      {/* ── Aviso mobile (oculto em sm+) ── */}
      <div className="sm:hidden bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p>O preview é otimizado para desktop.</p>
        <p className="mt-1">Use os botões acima para gerar o PDF.</p>
      </div>

      <div className="hidden sm:block" style={{ background: '#f3f4f6', padding: '24px 0' }}>
        {vendedores.map((vend, i) => (
          <div key={vend.id || i} style={{ maxWidth: 960, margin: '0 auto 24px', background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.10)', borderRadius: 8, overflow: 'hidden' }}>
            <MapaPageV2 vend={vend} leilao={leilao} organizador={organizador} colunasAtivas={colunasAtivas} />
          </div>
        ))}
      </div>

      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Mapa do Vendedor V2 — {leilao.nome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => downloadPdf(pdfUrl, pdfFilename)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Salvar PDF
                </button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button onClick={closePdfModal} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title={pdfFilename} style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}

      {/* Modal Alterar Comissão */}
      {showAlterarComissao && vendedores.length === 1 && (
        <AlterarComissaoModal
          tipo="venda"
          lotes={(vendedores[0].lotes_venda || []).filter(l => l.id)}
          organizadorId={orgId}
          onClose={() => setShowAlterarComissao(false)}
          onSaved={() => { if (onReload) onReload() }}
        />
      )}

      {/* Modal Adicionar Despesa */}
      {showDespesa && vendedores.length === 1 && (
        <AdicionarDespesaModal
          lotes={[vendedores[0].lotes_venda, vendedores[0].lotes_defesa, vendedores[0].lotes_nao_movimentados]
            .map(arr => (arr || []).filter(l => l.id))
            .find(arr => arr.length > 0) || []}
          organizadorId={orgId}
          onClose={() => setShowDespesa(false)}
          onSaved={() => { if (onReload) onReload() }}
        />
      )}

      {/* Modal Isentar/Desfazer Defesa */}
      {showIsencao && vendedores.length === 1 && (
        <IsentarDefesaModal
          modo={showIsencao}
          lotesDefesa={vendedores[0].lotes_defesa || vendedores[0].lotes_nao_movimentados?.filter(l => l.situacao === 'DEFESA') || []}
          organizadorId={orgId}
          onClose={() => setShowIsencao(null)}
          onSaved={() => { setShowIsencao(null); if (onReload) onReload() }}
        />
      )}

      {/* Modal Aplicar Isenção (sugestão em lote pro leilão inteiro) */}
      {showAplicarIsencao && (
        <AplicarIsencaoModal
          organizadorId={orgId}
          leilaoId={leilaoId}
          onClose={() => setShowAplicarIsencao(false)}
          onSaved={() => { setShowAplicarIsencao(false); if (onReload) onReload() }}
        />
      )}
    </>
  )
}
